// ══════════════════════════════════════════════════════════════
// IMPORTADOR DE XP DESDE EXCEL (agregado 27/09/2026, v2 28/09/2026)
//
// Lógica pura, sin React ni Firestore, para poder testearla sola.
// Flujo: hojas crudas del Excel -> parseWorkbook() -> buildXpImportPlan()
// El componente XpImportTab.tsx muestra el plan y lo aplica.
//
// Formato esperado: un Excel exportado del campus, con UNA HOJA POR
// MATERIA. Cada hoja trae una fila por alumno con sus notas de las
// actividades (ARO 1..8), parciales, recuperatorios y, si existe, final.
// Una nota >= NOTA_APROBACION suma la XP de la tabla XP_POR_EVENTO.
//
// El Excel es la fuente de verdad de la XP académica. Se guarda en
// Usuarios/{uid}.XpAcademica y se RECALCULA completa en cada
// importación, así subir dos veces la misma planilla no duplica nada.
// La XP de otras fuentes (eventos, misiones) se conserva como "extra":
//   extra      = CurrentExp anterior - XpAcademica anterior
//   CurrentExp = XpAcademica nueva + extra
// ══════════════════════════════════════════════════════════════

import type { Task, UserSkillRecord } from "../firebase-config";
import { getLevelFromExp } from "./levels";

// ---------- Configuración (editar acá) ----------

/** Nota mínima para considerar aprobada una actividad o examen. */
export const NOTA_APROBACION = 6;

/** XP que suma cada cosa aprobada, por materia. */
export const XP_POR_EVENTO = {
  actividad: 400,
  parcial1: 600,
  parcial2: 750,
  final: 1200,
};

export interface XpAreaConfig {
  /** Nombre con el que se crea la skill si el usuario todavía no la tiene. */
  skillLabel: string;
  /** Nombres de skill existentes que se consideran la misma área. */
  skillAliases: string[];
}

export const XP_AREAS: XpAreaConfig[] = [
  {
    skillLabel: "Programación",
    skillAliases: ["programming", "programacion", "programación", "prog"],
  },
  { skillLabel: "Arte", skillAliases: ["art", "arte"] },
  { skillLabel: "Diseño", skillAliases: ["design", "diseño", "diseno"] },
];

export interface AreaShare {
  area: string;
  /** Proporción de la XP de la materia que va a esta área (0 a 1). */
  share: number;
}

/**
 * A qué área (skill del perfil) suma la XP de cada materia.
 * `match` se compara contra el nombre de la hoja, sin acentos ni mayúsculas.
 * Una materia puede repartirse entre varias áreas: los `share` de cada
 * materia tienen que sumar 1.
 * Una hoja que no coincide con ninguna crea su propia skill con el nombre
 * de la hoja (y la vista previa lo avisa).
 */
export const MATERIA_A_AREA: { match: string; areas: AreaShare[] }[] = [
  { match: "programacion", areas: [{ area: "Programación", share: 1 }] },
  {
    match: "motores",
    areas: [
      { area: "Programación", share: 0.5 },
      { area: "Arte", share: 0.5 },
    ],
  },
  { match: "game design", areas: [{ area: "Diseño", share: 1 }] },
  { match: "audio", areas: [{ area: "Arte", share: 1 }] },
];

// ---------- Helpers de texto y números ----------

/** minúsculas, sin acentos, sin espacios repetidos. */
export const normalizeText = (value: unknown): string =>
  String(value ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/\s+/g, " ")
    .trim();

export const normalizeEmail = (value: unknown): string =>
  String(value ?? "").trim().toLowerCase();

/**
 * Acepta números reales o texto tipo "7", "7,5" o "1.250".
 * Devuelve null si la celda está vacía, es "-" o no es un número.
 */
export const parseNumberCell = (value: unknown): number | null => {
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  if (value === null || value === undefined) return null;
  let text = String(value).trim().replace(/\s/g, "");
  if (!text) return null;
  if (/^-?\d{1,3}(\.\d{3})+$/.test(text)) text = text.replace(/\./g, "");
  text = text.replace(",", ".");
  const parsed = Number(text);
  return Number.isFinite(parsed) ? parsed : null;
};

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const areasForSheet = (sheetName: string): AreaShare[] => {
  const n = normalizeText(sheetName);
  return (
    MATERIA_A_AREA.find((m) => n.includes(m.match))?.areas ?? [
      { area: sheetName.trim(), share: 1 },
    ]
  );
};

/**
 * Reparte la XP de una materia entre sus áreas. Redondea cada parte y le
 * da el resto a la última, para que la suma sea exactamente la XP total.
 */
export const splitXp = (xp: number, shares: AreaShare[]): Record<string, number> => {
  const result: Record<string, number> = {};
  let assigned = 0;
  shares.forEach((s, index) => {
    const part =
      index === shares.length - 1 ? xp - assigned : Math.round(xp * s.share);
    assigned += part;
    result[s.area] = (result[s.area] ?? 0) + part;
  });
  return result;
};

// ---------- Clasificación de columnas ----------

export type ColumnKind =
  | { kind: "actividad"; numero: number }
  | { kind: "parcial1" | "recuperatorio1" | "parcial2" | "recuperatorio2" | "final" }
  | { kind: "email" }
  | { kind: "nombre" }
  | { kind: "apellido" }
  | { kind: "ignorar" };

const refersTo = (n: string, which: 1 | 2) =>
  which === 1
    ? /\b1\b/.test(n) || n.includes("primer")
    : /\b2\b/.test(n) || n.includes("segundo");

/**
 * Decide qué representa una columna mirando su encabezado. Los nombres
 * cambian entre materias ("Parcial 1", "Primer Parcial - Obligatorio",
 * "ARO - 3 (clase 6)"...), por eso se buscan palabras clave.
 */
export const classifyColumn = (header: string): ColumnKind => {
  const n = normalizeText(header);
  if (!n) return { kind: "ignorar" };
  if (n.includes("correo") || n === "email" || n === "mail") return { kind: "email" };
  if (n === "nombre") return { kind: "nombre" };
  if (n.startsWith("apellido")) return { kind: "apellido" };
  // Totales y promedios los calcula el campus: los ignoramos y contamos nosotros.
  if (n.startsWith("total") || n.includes("promedio")) return { kind: "ignorar" };

  const aro = n.match(/\baro\s*-?\s*(\d+)/);
  if (aro) return { kind: "actividad", numero: Number(aro[1]) };

  if (n.includes("recuperatorio")) {
    if (refersTo(n, 1)) return { kind: "recuperatorio1" };
    if (refersTo(n, 2)) return { kind: "recuperatorio2" };
    return { kind: "ignorar" };
  }
  if (n.includes("parcial")) {
    if (refersTo(n, 1)) return { kind: "parcial1" };
    if (refersTo(n, 2)) return { kind: "parcial2" };
    return { kind: "ignorar" };
  }
  if (/\bfinal\b/.test(n)) return { kind: "final" };
  return { kind: "ignorar" };
};

// ---------- Lectura de hojas ----------

export interface RawSheet {
  name: string;
  headers: string[];
  rows: Record<string, unknown>[];
}

export interface SheetReport {
  sheet: string;
  areas: AreaShare[];
  /** true si la hoja no está en MATERIA_A_AREA y creó su propia skill. */
  unmappedArea: boolean;
  actividades: number;
  tieneParcial1: boolean;
  tieneRecuperatorio1: boolean;
  tieneParcial2: boolean;
  tieneRecuperatorio2: boolean;
  tieneFinal: boolean;
  alumnos: number;
  /** Error que impide usar la hoja (por ejemplo, sin columna de correo). */
  error?: string;
  /** Hojas vacías o que no parecen de notas se saltean sin error. */
  skipped?: boolean;
}

export interface MateriaResult {
  materia: string;
  /** XP de esta materia repartida por área. */
  reparto: Record<string, number>;
  actividadesAprobadas: number;
  actividadesTotales: number;
  parcial1: boolean;
  parcial2: boolean;
  final: boolean;
  xp: number;
}

export interface ParsedXpRow {
  /** Clave de la fila: el correo normalizado. */
  email: string;
  nombre: string;
  materias: MateriaResult[];
  /** XP por área (skill del perfil). */
  areas: Record<string, number>;
  /** Suma de todas las materias: es lo que se guarda como XpAcademica. */
  totalExp: number;
  warnings: string[];
  errors: string[];
}

export interface ParseResult {
  rows: ParsedXpRow[];
  sheets: SheetReport[];
  /** Si ninguna hoja se pudo usar, el archivo no sirve. */
  fatalError?: string;
}

const isApproved = (grade: number | null) =>
  grade !== null && grade >= NOTA_APROBACION;

const maxGrade = (grades: (number | null)[]) =>
  grades.reduce<number | null>(
    (best, g) => (g === null ? best : best === null ? g : Math.max(best, g)),
    null,
  );

export const parseWorkbook = (sheets: RawSheet[]): ParseResult => {
  const byEmail = new Map<string, ParsedXpRow>();
  const reports: SheetReport[] = [];

  sheets.forEach((sheet) => {
    const kinds = sheet.headers.map((h) => ({ header: h, ...classifyColumn(h) }));
    const cols = (k: string) => kinds.filter((c) => c.kind === k).map((c) => c.header);
    const emailCol = cols("email")[0];
    const actividadCols = kinds.filter(
      (c): c is { header: string; kind: "actividad"; numero: number } =>
        c.kind === "actividad",
    );
    const areas = areasForSheet(sheet.name);
    const report: SheetReport = {
      sheet: sheet.name,
      areas,
      unmappedArea: !MATERIA_A_AREA.some((m) =>
        normalizeText(sheet.name).includes(m.match),
      ),
      actividades: new Set(actividadCols.map((c) => c.numero)).size,
      tieneParcial1: cols("parcial1").length > 0,
      tieneRecuperatorio1: cols("recuperatorio1").length > 0,
      tieneParcial2: cols("parcial2").length > 0,
      tieneRecuperatorio2: cols("recuperatorio2").length > 0,
      tieneFinal: cols("final").length > 0,
      alumnos: 0,
    };
    reports.push(report);

    const hasGradeColumns =
      report.actividades > 0 || report.tieneParcial1 || report.tieneParcial2 || report.tieneFinal;
    if (!hasGradeColumns) {
      report.skipped = true;
      return;
    }
    if (!emailCol) {
      report.error = "No tiene columna de correo.";
      return;
    }

    const seenInSheet = new Set<string>();
    const nombreCol = cols("nombre")[0];
    const apellidoCol = cols("apellido")[0];

    sheet.rows.forEach((raw, index) => {
      const isEmpty = Object.values(raw).every(
        (v) => v === null || v === undefined || String(v).trim() === "",
      );
      if (isEmpty) return;

      const email = normalizeEmail(raw[emailCol]);
      const nombre = [nombreCol && raw[nombreCol], apellidoCol && raw[apellidoCol]]
        .filter(Boolean)
        .map((v) => String(v).trim())
        .join(" ");
      const key = email || `(sin correo: ${sheet.name} fila ${index + 2})`;

      let row = byEmail.get(key);
      if (!row) {
        row = { email, nombre, materias: [], areas: {}, totalExp: 0, warnings: [], errors: [] };
        byEmail.set(key, row);
      }
      if (!row.nombre && nombre) row.nombre = nombre;

      if (!email) {
        row.errors.push(`Falta el correo (hoja "${sheet.name}", fila ${index + 2}).`);
        return;
      }
      if (!EMAIL_PATTERN.test(email)) {
        row.errors.push(`Correo con formato inválido en la hoja "${sheet.name}".`);
        return;
      }
      if (seenInSheet.has(email)) {
        row.errors.push(`Aparece dos veces en la hoja "${sheet.name}".`);
        return;
      }
      seenInSheet.add(email);
      report.alumnos += 1;

      const grade = (header: string) => parseNumberCell(raw[header]);
      const gradesOf = (k: string) => cols(k).map(grade);

      // Cada ARO cuenta una sola vez aunque la columna aparezca repetida.
      const aprobadas = new Set(
        actividadCols.filter((c) => isApproved(grade(c.header))).map((c) => c.numero),
      ).size;
      // Parcial aprobado en la instancia original O en el recuperatorio: suma una vez.
      const p1 = isApproved(maxGrade([...gradesOf("parcial1"), ...gradesOf("recuperatorio1")]));
      const p2 = isApproved(maxGrade([...gradesOf("parcial2"), ...gradesOf("recuperatorio2")]));
      const fin = isApproved(maxGrade(gradesOf("final")));

      const xp =
        aprobadas * XP_POR_EVENTO.actividad +
        (p1 ? XP_POR_EVENTO.parcial1 : 0) +
        (p2 ? XP_POR_EVENTO.parcial2 : 0) +
        (fin ? XP_POR_EVENTO.final : 0);

      const reparto = splitXp(xp, areas);
      row.materias.push({
        materia: sheet.name,
        reparto,
        actividadesAprobadas: aprobadas,
        actividadesTotales: report.actividades,
        parcial1: p1,
        parcial2: p2,
        final: fin,
        xp,
      });
      const target = row;
      Object.entries(reparto).forEach(([area, value]) => {
        target.areas[area] = (target.areas[area] ?? 0) + value;
      });
      row.totalExp += xp;
    });
  });

  // Aviso útil: alumno que figura en algunas materias pero no en todas.
  const usableSheets = reports.filter((r) => !r.error && !r.skipped).map((r) => r.sheet);
  byEmail.forEach((row) => {
    if (row.errors.length || usableSheets.length < 2) return;
    const missing = usableSheets.filter((s) => !row.materias.some((m) => m.materia === s));
    if (missing.length) row.warnings.push(`No figura en: ${missing.join(", ")}.`);
  });

  const fatalError = usableSheets.length
    ? undefined
    : "Ninguna hoja tiene columnas de notas (ARO, parciales o final) con correo.";

  return { rows: Array.from(byEmail.values()), sheets: reports, fatalError };
};

// ---------- Plan de importación ----------

export type XpPlanStatus = "update" | "unchanged" | "not_found" | "invalid";

export interface XpSnapshot {
  currentExp: number;
  nivel: number;
  xpAcademica: number | null;
  skills: UserSkillRecord[];
}

export interface XpPlanItem {
  row: ParsedXpRow;
  status: XpPlanStatus;
  user?: Task;
  before?: XpSnapshot;
  after?: XpSnapshot;
  /** XP de eventos/misiones que se conserva por encima del Excel. */
  extraXp: number;
  /** Diferencia de CurrentExp (puede ser negativa si el Excel bajó). */
  deltaExp: number;
}

export interface XpImportOptions {
  /**
   * Primera importación de un usuario (todavía no tiene XpAcademica):
   * si su XP actual es mayor que la del Excel, conservar la diferencia
   * como XP extra en vez de bajarlo.
   */
  keepManualXpOnFirstImport: boolean;
}

const aliasesForArea = (area: string) =>
  (XP_AREAS.find((a) => a.skillLabel === area)?.skillAliases ?? [area]).map(
    normalizeText,
  );

/** Actualiza (o agrega) las skills de las áreas, sin tocar las demás. */
export const mergeSkills = (
  current: UserSkillRecord[],
  areas: Record<string, number>,
): UserSkillRecord[] => {
  const next = current.map((s) => ({ ...s }));
  // Las áreas configuradas siempre se escriben (en 0 si no hubo XP),
  // para que el perfil muestre las tres barras.
  const allAreas = new Set([...XP_AREAS.map((a) => a.skillLabel), ...Object.keys(areas)]);
  allAreas.forEach((area) => {
    const value = areas[area] ?? 0;
    const aliases = aliasesForArea(area);
    const index = next.findIndex((s) => aliases.includes(normalizeText(s.Materia)));
    if (index >= 0) next[index].Progress = value;
    else next.push({ Materia: area, Progress: value });
  });
  return next;
};

const sameSkills = (a: UserSkillRecord[], b: UserSkillRecord[]) =>
  a.length === b.length &&
  a.every((s, i) => s.Materia === b[i].Materia && s.Progress === b[i].Progress);

export const buildXpImportPlan = (
  rows: ParsedXpRow[],
  users: Task[],
  options: XpImportOptions,
): XpPlanItem[] => {
  const usersByEmail = new Map<string, Task>();
  users.forEach((u) => {
    const email = normalizeEmail(u.Email);
    if (email) usersByEmail.set(email, u);
  });

  return rows.map((row): XpPlanItem => {
    if (row.errors.length) return { row, status: "invalid", extraXp: 0, deltaExp: 0 };

    const user = usersByEmail.get(row.email);
    if (!user) return { row, status: "not_found", extraXp: 0, deltaExp: 0 };

    const previousAcademic =
      typeof user.XpAcademica === "number" ? user.XpAcademica : null;
    const currentExp = user.CurrentExp ?? 0;

    let extraXp = 0;
    if (previousAcademic !== null) {
      extraXp = Math.max(0, currentExp - previousAcademic);
    } else if (options.keepManualXpOnFirstImport) {
      extraXp = Math.max(0, currentExp - row.totalExp);
    }

    const newExp = row.totalExp + extraXp;
    const before: XpSnapshot = {
      currentExp,
      nivel: user.Nivel ?? 1,
      xpAcademica: previousAcademic,
      skills: user.Skill ?? [],
    };
    const after: XpSnapshot = {
      currentExp: newExp,
      nivel: getLevelFromExp(newExp),
      xpAcademica: row.totalExp,
      skills: mergeSkills(user.Skill ?? [], row.areas),
    };

    const unchanged =
      before.currentExp === after.currentExp &&
      before.nivel === after.nivel &&
      before.xpAcademica === after.xpAcademica &&
      sameSkills(before.skills, after.skills);

    return {
      row,
      user,
      before,
      after,
      extraXp,
      deltaExp: newExp - currentExp,
      status: unchanged ? "unchanged" : "update",
    };
  });
};

/** Usuarios de Firestore que no aparecen en el Excel (solo informativo). */
export const findUsersMissingFromExcel = (rows: ParsedXpRow[], users: Task[]): Task[] => {
  const excelEmails = new Set(rows.map((r) => r.email));
  return users.filter(
    (u) =>
      u.Rol !== "admin" &&
      normalizeEmail(u.Email) &&
      !excelEmails.has(normalizeEmail(u.Email)),
  );
};
